<script lang="ts">
export default {
  name: 'AIVerificationResultsPage',
};
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import UIInput from '@/components/ui/UIInput.vue';
import {
  Clock,
  FileText,
  User,
  Loader,
  AlertCircle,
  History,
  BotIcon,
  CheckCircle2,
  Building2,
  ChevronDown,
  Search,
  ArrowLeft,
  ArrowRight,
  CircleChevronLeft,
  CircleChevronRight,
} from '@lucide/vue';
import type {
  AIPromptResultWithReviews,
  ModelInvocation,
  ResultsPageItem,
  ModelInvocationStatus,
  AIPromptMapping,
} from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import type { DocumentType } from '@/types/v2/Document';
import { aiService } from '@/services/AIService';
import { policyTypeService } from '@/services/PolicyTypeService';
import { documentService } from '@/services/v2/DocumentService';
import {
  useModelInvocation,
  useAggregateInvocationPolling,
  getInvocationStatusLabel as getStatusLabel,
  ACTIVE_INVOCATIONS_LIMIT,
} from '@/composables/useModelInvocation';
import ExtractionReviewShell from '@/components/document/ExtractionReviewShell.vue';
import ExternalPolicyTargetSummary from '@/components/v2/external_policy_upload/ExternalPolicyTargetSummary.vue';
import Toggle from '@/components/v2/Toggle.vue';
import AppButton from '@/components/v2/AppButton.vue';

const route = useRoute();
const router = useRouter();

// Server-side pagination state (Previous/Next over the completed-results history)
const PAGE_SIZE = 25;
const currentPage = ref(0); // zero-indexed
const totalCount = ref(0);
const offset = computed(() => currentPage.value * PAGE_SIZE);
const hasPrevPage = computed(() => currentPage.value > 0);
const hasNextPage = computed(() => (currentPage.value + 1) * PAGE_SIZE < totalCount.value);

// State
const isLoading = ref(false);
const error = ref<string>('');
const promptResults = ref<AIPromptResultWithReviews[]>([]);
const activeInvocations = ref<ModelInvocation[]>([]);
const selectedResult = ref<AIPromptResultWithReviews | null>(null);
// Highlights the row you last opened after returning to the list (selectedResult is
// null on the list view, so it can't drive the highlight).
const lastViewedResultId = ref<number | null>(null);
// Allowed targets for the coverage-line add/recategorize pickers: the linked policy's
// uniquely-lined coverage types, falling back to the mapped prompt schema's templates.
// Comes from the merged endpoint; empty hides the structural line controls.

// Account-name search filter state
const accountNameSearch = ref<string>(''); // bound to the input (updates immediately)
const debouncedAccountSearch = ref<string>(''); // drives filtering (updated after a pause)

// User-association filter state (server-side)
const filterServicingOnly = ref(false);
const filterFavoritedOnly = ref(false);
// When off (default) already-reviewed/validated results are excluded from the queue.
const showValidated = ref(false);
const hasUserAssociationFilters = computed(() => filterServicingOnly.value || filterFavoritedOnly.value);

// Reference data used by the Account chip to decide when to apply a coverage-type filter
const coverageTypes = ref<CoverageType[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const promptMappings = ref<AIPromptMapping[]>([]);

// Polling state
const { pollInvocationStatus, stopPollingInvocation, stopAllPolling } = useModelInvocation();

// Per-invocation polling vs one aggregate refresh loop for large batches (CAC-5607).
const { isAggregatePolling, syncPollingMode } =
  useAggregateInvocationPolling({
    activeInvocations,
    onFinished: () => loadResults(),
    startIndividualPoll: (invocationId) => startPollingInvocation(invocationId),
    stopAllIndividualPolls: () => stopAllPolling(),
    // This page has no extraction-failures list, so a FAILED invocation has nowhere
    // else to show up.
    retainFailures: true,
  });

// Field data composable

// Type guards
const isInvocation = (item: ResultsPageItem): item is ModelInvocation => {
  return 'invocationId' in item && 'status' in item;
};

const isPromptResult = (item: ResultsPageItem): item is AIPromptResultWithReviews => {
  return 'id' in item && 'rawOutput' in item;
};

// Combined results + invocations. Active invocations are transient/live, so they only
// appear on the first page.
const allResults = computed(() => {
  const items: ResultsPageItem[] = [];

  // Completed results (the current server page)
  if (promptResults.value && promptResults.value.length > 0) {
    items.push(...promptResults.value);
  }

  // Active invocations on the first page only
  if (currentPage.value === 0 && activeInvocations.value && activeInvocations.value.length > 0) {
    items.push(...activeInvocations.value);
  }

  // Sort by created date descending
  return items.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA;
  });
});

// Account-name search is server-side now, so only the malformed guard remains here.
const sortedResults = computed(() => {
  return allResults.value.filter((r) => {
    if (isInvocation(r)) return true;
    if (isPromptResult(r)) return !r.isMalformed;
    return true;
  });
});

// Bottom-fade indicator: show a gradient at the bottom of the scrollable list while there
// is more to scroll to, and hide it once the list is scrolled to the end.
const listScrollEl = ref<HTMLElement | null>(null);
const canScrollDown = ref(false);
const updateListScroll = () => {
  const el = listScrollEl.value;
  canScrollDown.value = !!el && el.scrollTop + el.clientHeight < el.scrollHeight - 1;
};
// Recompute when the rendered rows change or we switch between list and detail views.
watch([sortedResults, selectedResult], () => nextTick(updateListScroll));

// Select a result by id. Prefers the currently-loaded page, and falls back to a direct
// fetch so deep links (e.g., the Unvalidated link on the account Policies tab) still open
// the detail view when the target result isn't on page 1.
const selectResultById = async (id: number) => {
  let match: AIPromptResultWithReviews | undefined = promptResults.value.find((r) => r.id === id);
  if (!match) {
    try {
      match = await aiService.getPromptResultWithReviews(id);
    } catch (err) {
      console.error(`Failed to fetch prompt result ${id}:`, err);
      return;
    }
  }
  if (match) {
    lastViewedResultId.value = match.id;
    selectedResult.value = match;
  }
};

// Read once, synchronously, so first render can hide the list block before it briefly
// paints between loadResults finishing and the deep-linked result being selected.
const initialResultId = (() => {
  const p = route.query.resultId;
  if (typeof p !== 'string') return null;
  const n = parseInt(p, 10);
  return isNaN(n) ? null : n;
})();
const isOpeningInitialResult = ref(initialResultId != null);

// Lifecycle
onMounted(async () => {
  window.addEventListener('resize', updateListScroll);
  await Promise.all([loadResults(), loadActiveInvocations(), loadAccountChipReferenceData()]);

  if (initialResultId != null) {
    await selectResultById(initialResultId);
    isOpeningInitialResult.value = false;
  }
});

onUnmounted(() => {
  stopAllPolling();
  window.removeEventListener('resize', updateListScroll);
});

// Sync selectedResult to URL query params
watch(selectedResult, (newResult) => {
  const query = { ...route.query };
  if (newResult) {
    const idStr = String(newResult.id);
    if (query.resultId !== idStr) {
      query.resultId = idStr;
      router.replace({ query });
    }
  } else if (query.resultId) {
    delete query.resultId;
    router.replace({ query });
  }
});

// Watch for route query changes to handle resultId from browser back/forward
watch(
  () => route.query.resultId,
  async (newResultId) => {
    if (newResultId && typeof newResultId === 'string') {
      const id = parseInt(newResultId, 10);
      if (!isNaN(id) && selectedResult.value?.id !== id) {
        await selectResultById(id);
      }
    } else if (!newResultId && selectedResult.value) {
      selectedResult.value = null;
    }
  },
);

// Watch active invocations to auto-start/stop polling
watch(
  activeInvocations,
  (newInvocations, oldInvocations) => {
    // Aggregate mode owns refresh for large batches — no per-invocation polls. This
    // watcher is deep and the poll callbacks mutate statuses in place, so it fires on
    // every status tick.
    if (isAggregatePolling()) return;
    // Start polling for new invocations that need it
    if (newInvocations && Array.isArray(newInvocations)) {
      newInvocations.forEach((inv) => {
        if (inv.status === 'PENDING' || inv.status === 'RUNNING') {
          // Check if this is a new invocation or status changed to PENDING/RUNNING
          const oldInv = oldInvocations?.find((old) => old.invocationId === inv.invocationId);
          if (!oldInv || (oldInv.status !== 'PENDING' && oldInv.status !== 'RUNNING')) {
            startPollingInvocation(inv.invocationId);
          }
        }
      });
    }
  },
  { deep: true },
);

watch([filterServicingOnly, filterFavoritedOnly, showValidated], () => {
  reloadFromFirstPage();
});

// Debounce the account-name search so the input stays responsive and we only issue a new
// server request once the user pauses typing.
let accountSearchDebounce: ReturnType<typeof setTimeout> | null = null;
watch(accountNameSearch, (val) => {
  if (accountSearchDebounce) clearTimeout(accountSearchDebounce);
  accountSearchDebounce = setTimeout(() => {
    debouncedAccountSearch.value = val;
  }, 300);
});

// A settled account search reloads from the first page.
watch(debouncedAccountSearch, () => {
  reloadFromFirstPage();
});

// Functions
const loadResults = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    const response = await aiService.getPromptResultsWithReviews({
      pageSize: PAGE_SIZE,
      pageOffset: offset.value,
      userHasServicingRole: filterServicingOnly.value || undefined,
      userFavorited: filterFavoritedOnly.value || undefined,
      showValidated: showValidated.value || undefined,
      accountSearch: debouncedAccountSearch.value.trim() || undefined,
    });
    promptResults.value = response.results;
    totalCount.value = response.totalCount;
  } catch (err: any) {
    error.value = err.message || 'Failed to load results';
    promptResults.value = [];
    totalCount.value = 0;
    console.error('Failed to load results:', err);
  } finally {
    isLoading.value = false;
  }
};

// Reset to the first page on a filter change. Changing the page triggers the reload
// watcher; if already on page 0 there's no change, so reload directly.
const reloadFromFirstPage = () => {
  if (currentPage.value !== 0) {
    currentPage.value = 0;
  } else {
    loadResults();
  }
};

const goToPreviousPage = () => {
  if (hasPrevPage.value) currentPage.value -= 1;
};

const goToNextPage = () => {
  if (hasNextPage.value) currentPage.value += 1;
};

// Reload whenever the page changes (Previous/Next).
watch(currentPage, () => {
  loadResults();
});

const loadActiveInvocations = async () => {
  try {
    const invocations = await aiService.getActiveInvocations(ACTIVE_INVOCATIONS_LIMIT);
    activeInvocations.value = invocations || [];
    // Sole owner of the polling strategy: aggregate loop for large batches, per-invocation
    // polls otherwise.
    syncPollingMode();
  } catch (err: any) {
    console.error('Failed to load active invocations:', err);
    activeInvocations.value = [];
  }
};

const startPollingInvocation = (invocationId: string) => {
  pollInvocationStatus(
    invocationId,
    async (statusResponse) => {
      if (statusResponse.status === 'COMPLETED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Remove from active invocations
        if (activeInvocations.value) {
          activeInvocations.value = activeInvocations.value.filter((inv) => inv.invocationId !== invocationId);
        }

        // Fetch the result and add to promptResults
        try {
          const result = await aiService.getInvocationResult(invocationId);
          // Add default review values since this is a new result
          const resultWithReviews: AIPromptResultWithReviews = {
            ...result,
            reviews: [],
            reviewCount: 0,
          };
          promptResults.value.unshift(resultWithReviews);
        } catch (err) {
          console.error('Failed to get invocation result:', err);
        }
      } else if (statusResponse.status === 'FAILED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Update the invocation in the list
        if (activeInvocations.value) {
          const invIndex = activeInvocations.value.findIndex((inv) => inv.invocationId === invocationId);
          if (invIndex !== -1) {
            activeInvocations.value[invIndex].status = 'FAILED';
            if (statusResponse.errorMessage) {
              activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
            }
          }
        }
      }
    },
    (error) => {
      console.error('Polling error:', error);
      // Continue polling on error
    },
    (statusResponse) => {
      // Update status in active invocations
      if (activeInvocations.value) {
        const invIndex = activeInvocations.value.findIndex((inv) => inv.invocationId === invocationId);
        if (invIndex !== -1) {
          activeInvocations.value[invIndex].status = statusResponse.status;
          if (statusResponse.errorMessage) {
            activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
          }
        }
      }
    },
  );
};

const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

const getModelDisplayName = (modelArn: string) => {
  const parts = modelArn.split('/');
  return parts[parts.length - 1] || modelArn;
};

// Open a result's detail view. In-progress invocations have no detail to show;
// a completed invocation maps to its prompt result.
const openResult = (item: ResultsPageItem) => {
  let result: AIPromptResultWithReviews | undefined;

  if (isInvocation(item)) {
    if (item.status === 'COMPLETED' && item.promptResultId) {
      result = promptResults.value.find((r) => r.id === item.promptResultId);
    }
  } else if (isPromptResult(item)) {
    result = item;
  }

  if (!result) return;
  lastViewedResultId.value = result.id;
  selectedResult.value = result;
};

// Return from the detail view to the list. The watcher clears the document/fields and the
// URL sync removes ?resultId; lastViewedResultId re-highlights the row that was opened.
const handleBackToList = () => {
  selectedResult.value = null;
};

const canOpenResult = (item: ResultsPageItem): boolean => {
  if (isInvocation(item)) return item.status === 'COMPLETED' && !!item.promptResultId;
  return isPromptResult(item);
};

const isLastViewed = (item: ResultsPageItem) => {
  if (lastViewedResultId.value == null) return false;
  if (isInvocation(item)) {
    return item.status === 'COMPLETED' && item.promptResultId === lastViewedResultId.value;
  }
  return isPromptResult(item) && item.id === lastViewedResultId.value;
};

const getItemKey = (item: ResultsPageItem): string => {
  if (isInvocation(item)) {
    return `invocation-${item.invocationId}`;
  }
  return `result-${item.id}`;
};

const getStatusVariant = (status: ModelInvocationStatus): 'outline' | 'destructive' => {
  switch (status) {
    case 'PENDING':
      return 'outline';
    case 'RUNNING':
      return 'outline';
    case 'COMPLETED':
      return 'outline';
    case 'FAILED':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusBadgeClass = (status: ModelInvocationStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'border-yellow-200 bg-yellow-50 text-yellow-700';
    case 'RUNNING':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    case 'COMPLETED':
      return 'border-green-200 bg-green-50 text-green-700';
    case 'FAILED':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return '';
  }
};

// An external-designated prompt creates a policy through the binder flow. Prompt results,
// in-flight invocations and failures all carry the designation -- it comes off the prompt,
// so a binder is marked External while it is still scanning and if it never finishes.
const isExternalResult = (item: ResultsPageItem | AIPromptResultWithReviews | null) => {
  if (!item) return false;
  return 'designation' in item && item.designation === 'external';
};

const handleReviewCompleted = async () => {
  // Refresh results to show updated review info
  await loadResults();
  // Also refresh the selected result's data if still selected
  if (selectedResult.value) {
    const updatedResult = promptResults.value.find((r) => r.id === selectedResult.value?.id);
    if (updatedResult) {
      selectedResult.value = updatedResult;
    }
  }
};

const formatReviewDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toRelative();
};

const loadAccountChipReferenceData = async () => {
  try {
    const [ctypes, dtypes, mappings] = await Promise.all([
      policyTypeService.GetDistinctCoverageTypes(),
      documentService.getDocumentTypes(),
      aiService.getPromptMappings(),
    ]);
    coverageTypes.value = ctypes;
    documentTypes.value = dtypes;
    promptMappings.value = mappings;
  } catch (e) {
    console.error('Failed to load account chip reference data', e);
  }
};

// Precompute coverage-type name keyed by `${promptId}:${promptVersion}` so each row's
// account chip is an O(1) lookup instead of scanning mappings/docTypes/coverageTypes
// on every render (called per row, twice, across the full results list).
const covNameByPromptKey = computed(() => {
  const docTypeById = new Map(documentTypes.value.map((dt) => [dt.id, dt]));
  const covTypeById = new Map(coverageTypes.value.map((c) => [c.coverageTypeId, c]));
  const map = new Map<string, string>();
  for (const mapping of promptMappings.value) {
    const docType = docTypeById.get(mapping.documentTypeId);
    if (docType?.domainName === 'Policy') {
      const ct = covTypeById.get(mapping.coverageTypeId);
      if (ct) map.set(`${mapping.promptId}:${mapping.promptVersion}`, ct.coverageType);
    }
  }
  return map;
});

const getAccountChipInfo = (result: AIPromptResultWithReviews): { accountId: number; accountName: string; covName?: string } | null => {
  if (!result.documentAccountId || !result.documentAccountName) return null;

  const info: { accountId: number; accountName: string; covName?: string } = {
    accountId: result.documentAccountId,
    accountName: result.documentAccountName,
  };

  const covName = covNameByPromptKey.value.get(`${result.promptId}:${result.promptVersion}`);
  if (covName) info.covName = covName;
  return info;
};

const goToAccountPolicies = (result: AIPromptResultWithReviews, event: Event) => {
  event.stopPropagation();
  const info = getAccountChipInfo(result);
  if (!info) return;
  const query: Record<string, string> = {};
  if (info.covName) query.covName = info.covName;
  router.push({ path: `/account/${info.accountId}/policy-detail`, query });
};
</script>

<template>
  <div class="flex h-full flex-col overflow-hidden bg-linear-to-br from-slate-50 to-blue-50 p-4">
    <div class="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-3">
      <!-- Opening a deep-linked result: hide the list while resolving so it doesn't
           briefly render between loadResults finishing and selectedResult being set. -->
      <div
        v-if="isOpeningInitialResult && !selectedResult"
        class="flex min-h-0 flex-1 items-center justify-center">
        <Loader class="mr-2 h-6 w-6 animate-spin text-blue-600" />
        <span class="text-slate-600">Loading result...</span>
      </div>

      <div
        v-else-if="!selectedResult"
        class="flex min-h-0 flex-1 flex-col gap-3">
        <!-- Page header (outside the card) -->
        <div class="flex shrink-0 items-center gap-2 pl-2">
          <History class="h-6 w-6 text-blue-600" />
          <h1 class="text-2xl">AI Data Extraction Results</h1>
          <Badge
            v-if="totalCount > 0"
            variant="secondary">
            {{ totalCount }}
          </Badge>
        </div>

        <Card class="relative flex min-h-0 flex-1 flex-col border-slate-200 bg-white/80 backdrop-blur-sm">
          <CardHeader class="shrink-0 pb-2">
            <!-- Search + filters, inline -->
            <div class="flex flex-wrap items-center gap-4 px-2">
              <!-- Account Name Search -->
              <div
                v-if="totalCount > 0 || accountNameSearch"
                class="relative w-72 shrink-0">
                <Search class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <UIInput
                  v-model="accountNameSearch"
                  type="text"
                  placeholder="Search by account name"
                  class="h-10 border border-slate-400 pl-9" />
              </div>

              <!-- Filters -->
              <div
                class="ml-auto flex flex-wrap items-center gap-3 pr-2"
                @click.stop>
                <Toggle
                  id="filter-servicing"
                  label="My servicing policies only"
                  v-model="filterServicingOnly"
                  size="sm" />
                <Toggle
                  id="filter-favorited"
                  label="My favorited accounts only"
                  v-model="filterFavoritedOnly"
                  size="sm" />
                <Toggle
                  id="filter-validated"
                  label="Show Validated Results"
                  v-model="showValidated"
                  size="sm" />
              </div>
            </div>
          </CardHeader>

          <CardContent class="flex min-h-0 flex-1 flex-col">
            <!-- Loading State (only on initial/empty load — pagination keeps stale rows in place to avoid layout shift) -->
            <div
              v-if="isLoading && sortedResults.length === 0"
              class="flex items-center justify-center py-8">
              <Loader class="mr-2 h-6 w-6 animate-spin text-blue-600" />
              <span class="text-slate-600">Loading results...</span>
            </div>

            <!-- Error State -->
            <div
              v-else-if="error"
              class="flex items-center justify-center py-8">
              <AlertCircle class="mr-2 h-6 w-6 text-red-500" />
              <span class="text-red-600">{{ error }}</span>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="sortedResults.length === 0"
              class="py-12 text-center">
              <Clock class="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <template v-if="hasUserAssociationFilters">
                <h3 class="mb-2 text-lg font-medium text-slate-700">No Matching Results</h3>
                <p class="mx-auto max-w-sm text-sm text-slate-500">
                  No results found for your associated policies or favorited accounts. Try adjusting the filters above.
                </p>
              </template>
              <template v-else>
                <h3 class="mb-2 text-lg font-medium text-slate-700">No Results Yet</h3>
                <p class="mx-auto max-w-sm text-sm text-slate-500">No verified results available yet.</p>
              </template>
            </div>

            <!-- Results List (bottom fade hints there are more rows to scroll to) -->
            <div
              v-if="sortedResults.length > 0"
              class="relative min-h-0 flex-1">
              <div
                ref="listScrollEl"
                class="h-full space-y-2 overflow-y-auto"
                @scroll="updateListScroll">
                <div
                  v-for="item in sortedResults"
                  :key="getItemKey(item)"
                  :class="[
                    'flex items-center justify-between rounded-lg border p-3 transition-all',
                    isLastViewed(item)
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : isInvocation(item) && (item.status === 'PENDING' || item.status === 'RUNNING')
                        ? 'border-slate-200 bg-slate-50 opacity-75'
                        : isInvocation(item) && item.status === 'FAILED'
                          ? 'border-red-200 bg-red-50'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                  ]">
                  <div class="flex flex-1 flex-wrap items-center gap-4">
                    <!-- Status Icon (for invocations) -->
                    <div
                      v-if="isInvocation(item)"
                      class="flex items-center gap-1">
                      <Loader
                        v-if="item.status === 'RUNNING'"
                        class="h-4 w-4 animate-spin text-blue-600" />
                      <Clock
                        v-else-if="item.status === 'PENDING'"
                        class="h-4 w-4 animate-pulse text-yellow-600" />
                      <CheckCircle2
                        v-else-if="item.status === 'COMPLETED'"
                        class="h-4 w-4 text-green-600" />
                      <AlertCircle
                        v-else-if="item.status === 'FAILED'"
                        class="h-4 w-4 text-red-600" />
                    </div>

                    <!-- Created At -->
                    <div class="flex items-center gap-1 text-sm text-slate-500">
                      <Clock
                        v-if="!isInvocation(item) || item.status !== 'RUNNING'"
                        class="h-4 w-4" />
                      <span>{{ formatDate(item.createdAt) }}</span>
                    </div>

                    <!-- Status Badge (for invocations) -->
                    <div
                      v-if="isInvocation(item)"
                      class="flex items-center gap-1">
                      <Badge
                        :variant="getStatusVariant(item.status)"
                        :class="`rounded-md px-2 py-1 text-xs ${getStatusBadgeClass(item.status)}`">
                        {{ getStatusLabel(item.status) }}
                      </Badge>
                    </div>

                    <!-- Model -->
                    <div class="flex min-w-[50px] items-center gap-1">
                      <BotIcon class="h-4 w-4 text-blue-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ getModelDisplayName(item.modelArn) }}
                      </Badge>
                    </div>

                    <!-- Account -->
                    <div
                      v-if="isPromptResult(item) && getAccountChipInfo(item)"
                      class="flex min-w-[50px] items-center gap-1">
                      <Building2 class="h-4 w-4 text-indigo-500" />
                      <Badge
                        variant="outline"
                        class="cursor-pointer rounded-md px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50"
                        @click="goToAccountPolicies(item, $event)">
                        {{ getAccountChipInfo(item)!.accountName }}
                      </Badge>
                    </div>

                    <!-- Document -->
                    <div class="flex min-w-[50px] items-center gap-1">
                      <FileText class="h-4 w-4 text-green-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ item.documentName || 'No document' }}
                      </Badge>
                    </div>

                    <!-- External upload: this extraction creates a policy rather than
                         overriding one, at any stage of the run. Internal validation work
                         carries no badge. -->
                    <Badge
                      v-if="isExternalResult(item)"
                      variant="secondary"
                      class="rounded-md border-amber-300 bg-amber-100 px-2 py-1 text-xs text-amber-800"
                      data-testid="external-designation-badge">
                      External
                    </Badge>

                    <!-- User -->
                    <div class="flex min-w-[50px] items-center gap-1">
                      <User class="h-4 w-4 text-purple-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ item.userEmail }}
                      </Badge>
                    </div>

                    <!-- Error Message (for failed invocations) -->
                    <div
                      v-if="isInvocation(item) && item.status === 'FAILED' && item.errorMessage"
                      class="flex items-center gap-1 text-xs text-red-600">
                      <AlertCircle class="h-3 w-3" />
                      <span class="max-w-xs truncate">{{ item.errorMessage }}</span>
                    </div>

                    <!-- Right-aligned section: Review Status + Validation Badge -->
                    <div
                      v-if="isPromptResult(item)"
                      class="ml-auto flex items-center gap-3">
                      <!-- Review Status -->
                      <div
                        v-if="item.reviewCount > 0"
                        class="flex items-center gap-1">
                        <CheckCircle2 class="h-4 w-4 text-green-600" />
                        <span class="text-xs text-green-700"> Validated by {{ item.reviews[0]?.userEmail }} </span>
                        <span class="text-xs text-slate-400">
                          {{ formatReviewDate(item.reviews[0]?.reviewedAt) }}
                        </span>
                        <Badge
                          v-if="item.reviewCount > 1"
                          variant="outline"
                          class="rounded-full px-1.5 py-0 text-[10px]">
                          +{{ item.reviewCount - 1 }}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <!-- View action: full-height edge affordance that opens the detail view -->
                  <button
                    v-if="canOpenResult(item)"
                    type="button"
                    aria-label="View details"
                    title="View details"
                    class="group -my-3 -mr-3 ml-3 flex shrink-0 items-center self-stretch rounded-l-none rounded-r-lg border-l border-slate-200 px-4 text-slate-400 transition-colors duration-200 hover:bg-primary/10 hover:text-primary"
                    @click="openResult(item)">
                    <ArrowRight class="h-4 w-4" />
                  </button>
                </div>
              </div>
              <!-- Bottom fade: hidden once scrolled to the end -->
              <div
                v-show="canScrollDown"
                class="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-linear-to-t from-white to-transparent"></div>
              <!-- Scroll-more chevron (same canScrollDown signal). v-show sits on an element
                   with no display utility so Tailwind's `important` import can't defeat it. -->
              <ChevronDown
                v-show="canScrollDown"
                class="pointer-events-none absolute bottom-2 left-1/2 h-5 w-5 -translate-x-1/2 text-slate-400/50" />
            </div>

            <!-- Pagination (completed results) -->
            <div
              v-if="hasPrevPage || hasNextPage"
              class="flex shrink-0 gap-4 pt-4">
              <AppButton
                secondary
                :disabled="!hasPrevPage || isLoading"
                @click="goToPreviousPage"
                data-test="prev-button">
                <CircleChevronLeft
                  :size="20"
                  class="mr-2 text-primary" />
                Previous
              </AppButton>
              <AppButton
                secondary
                :disabled="!hasNextPage || isLoading"
                @click="goToNextPage"
                data-test="next-button">
                Next
                <CircleChevronRight
                  :size="20"
                  class="ml-2 text-primary" />
              </AppButton>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- Detail View: back navigation + metadata header + document/fields -->
      <div
        v-if="selectedResult"
        class="flex min-h-0 flex-1 flex-col gap-3">
        <!-- Back navigation -->
        <div class="flex shrink-0 items-center">
          <button
            type="button"
            class="btn gap-1 btn-sm"
            @click="handleBackToList">
            <ArrowLeft class="h-4 w-4" />
            Back to results
          </button>
        </div>

        <!-- Metadata header for the opened result -->
        <div class="shrink-0 rounded-lg border border-blue-500 bg-blue-50 p-3 shadow-sm">
          <div class="flex flex-wrap items-center gap-4">
            <!-- Created At -->
            <div class="flex items-center gap-1 text-sm text-slate-500">
              <Clock class="h-4 w-4" />
              <span>{{ formatDate(selectedResult.createdAt) }}</span>
            </div>

            <!-- Model -->
            <div class="flex items-center gap-1">
              <BotIcon class="h-4 w-4 text-blue-500" />
              <Badge
                variant="outline"
                class="rounded-md px-2 py-1 text-xs">
                {{ getModelDisplayName(selectedResult.modelArn) }}
              </Badge>
            </div>

            <!-- Account -->
            <div
              v-if="getAccountChipInfo(selectedResult)"
              class="flex items-center gap-1">
              <Building2 class="h-4 w-4 text-indigo-500" />
              <Badge
                variant="outline"
                class="cursor-pointer rounded-md px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50"
                @click="goToAccountPolicies(selectedResult, $event)">
                {{ getAccountChipInfo(selectedResult)!.accountName }}
              </Badge>
            </div>

            <!-- Document -->
            <div class="flex items-center gap-1">
              <FileText class="h-4 w-4 text-green-500" />
              <Badge
                variant="outline"
                class="rounded-md px-2 py-1 text-xs">
                {{ selectedResult.documentName || 'No document' }}
              </Badge>
            </div>

            <Badge
              v-if="isExternalResult(selectedResult)"
              variant="secondary"
              class="rounded-md border-amber-300 bg-amber-100 px-2 py-1 text-xs text-amber-800"
              data-testid="external-designation-badge">
              External
            </Badge>

            <!-- User -->
            <div class="flex items-center gap-1">
              <User class="h-4 w-4 text-purple-500" />
              <Badge
                variant="outline"
                class="rounded-md px-2 py-1 text-xs">
                {{ selectedResult.userEmail }}
              </Badge>
            </div>

            <!-- Right-aligned section: Review Status + Validation Badge -->
            <div class="ml-auto flex items-center gap-3">
              <!-- Review Status -->
              <div
                v-if="selectedResult.reviewCount > 0"
                class="flex items-center gap-1">
                <CheckCircle2 class="h-4 w-4 text-green-600" />
                <span class="text-xs text-green-700"> Validated by {{ selectedResult.reviews[0]?.userEmail }} </span>
                <span class="text-xs text-slate-400">
                  {{ formatReviewDate(selectedResult.reviews[0]?.reviewedAt) }}
                </span>
                <Badge
                  v-if="selectedResult.reviewCount > 1"
                  variant="outline"
                  class="rounded-full px-1.5 py-0 text-[10px]">
                  +{{ selectedResult.reviewCount - 1 }}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <!-- Document viewer and extracted fields -->
        <ExtractionReviewShell
          :result="selectedResult"
          @review-completed="handleReviewCompleted">
          <template #action-panel="{ target, entityMatches, entityValues }">
            <div
              v-if="target?.kind === 'new_external_policy'"
              class="shrink-0 rounded-lg border border-slate-200 bg-white/80 p-3 backdrop-blur-sm">
              <ExternalPolicyTargetSummary
                :target="target"
                :entity-matches="entityMatches"
                :entity-values="entityValues"
                show-review-link />
            </div>
          </template>
        </ExtractionReviewShell>
      </div>
    </div>
  </div>
</template>
